import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import {
  createProduct,
  updateProduct,
  deleteProduct,
  getProducts,
  getProduct,
  deleteProductImage,
  deleteProductVideo,
  createProductImage,
} from "data/server_apis/commodityService";
import { createGstThunk, updateGstThunk } from "./productGstSlice";
const productAdapter = createEntityAdapter({
  selectId: (product) => product.id,
});

const productImageAdapter = createEntityAdapter({
  selectId: (productImage) => productImage.id,
});

export const deleteProductImageThunk = createAsyncThunk(
  "productImage/delete",
  async ({ id, product }, { rejectWithValue }) => {
    try {
      return (await deleteProductImage(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const createProductImageThunk = createAsyncThunk(
  "productImage/create",
  async (data, { rejectWithValue }) => {
    try {
      return (await createProductImage(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
export const deleteProductVideoThunk = createAsyncThunk(
  "productVideo/delete",
  async ({ id, product }, { rejectWithValue }) => {
    try {
      return (await deleteProductVideo(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);
export const createProductThunk = createAsyncThunk(
  "product/create",
  async (data, { rejectWithValue }) => {
    try {
      return (await createProduct(data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getProductsThunk = createAsyncThunk(
  "product/getProducts",
  async (
    { category, subCategory, hsn, has_gst, page, page_size = "" },
    { rejectWithValue }
  ) => {
    try {
      return (
        await getProducts(category, subCategory, hsn, has_gst, page, page_size)
      ).data;
    } catch (err) {
      console.log(err);
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const getProductThunk = createAsyncThunk(
  "product/getProduct",
  async (id, { rejectWithValue }) => {
    try {
      return (await getProduct(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const refreshCurrentProductThunk = createAsyncThunk(
  "product/refreshCurrentProduct",
  async (id, { rejectWithValue }) => {
    try {
      return (await getProduct(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const deleteProductsThunk = createAsyncThunk(
  "product/deleteProducts",
  async (id, { rejectWithValue }) => {
    try {
      return (await deleteProduct(id)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

export const updateProductThunk = createAsyncThunk(
  "product/updateProduct",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return (await updateProduct(id, data)).data;
    } catch (err) {
      const customError = {
        name: err.response?.status,
        message: err.response?.status === 400 ? err.response?.data : null,
      };
      // console.log("Error", customError);
      return rejectWithValue(customError);
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState: productAdapter.getInitialState({
    loading: {},
    error: {},
    errorMessage: {},
    success: {},
    imageStatus: {
      loading: {},
      error: {},
      errorMessage: {},
      success: {},
    },
    videoStatus: {
      loading: {},
      error: {},
      errorMessage: {},
      success: {},
    },
    currentProduct: null,
    productPageCount: 1,
  }),
  reducers: {
    clearProduct(state, action) {
      state.currentProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProductThunk.pending, (state, action) => {
        state.loading = { create: true };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
        state.currentProduct = action.payload;
      })
      .addCase(createProductThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { create: true };
        state.success = {};
        state.errorMessage = { create: action.payload?.message };
      })
      .addCase(createProductThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { create: true };
        state.errorMessage = {};
        state.currentProduct = action.payload;
        productAdapter.addOne(state, action.payload);
      });
    builder
      .addCase(createProductImageThunk.pending, (state, action) => {
        state.imageStatus.loading = { create: true };
        state.imageStatus.error = {};
        state.imageStatus.success = {};
        state.imageStatus.errorMessage = {};
      })
      .addCase(createProductImageThunk.rejected, (state, action) => {
        state.imageStatus.loading = {};
        state.imageStatus.error = { create: true };
        state.imageStatus.success = {};
        state.imageStatus.errorMessage = { create: action.payload?.message };
      })
      .addCase(createProductImageThunk.fulfilled, (state, action) => {
        state.imageStatus.loading = {};
        state.imageStatus.error = {};
        state.imageStatus.success = { create: true };
        state.imageStatus.errorMessage = {};
        state.imageStatus.currentProduct = action.payload;
        productAdapter.updateOne(state, {
          id: action.payload.product,
          changes: {
            images: [
              ...(state.entities[action.payload.product]?.images || []),
              action.payload,
            ],
          },
        });
      });
    builder
      .addCase(getProductsThunk.pending, (state, action) => {
        state.loading = { list: true };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(getProductsThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { list: true };
        state.success = {};
        state.errorMessage = { list: action.payload?.message };
      })
      .addCase(getProductsThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { list: true };
        state.errorMessage = {};
        productAdapter.setAll(state, action.payload.results);
        state.productPageCount = action.payload.total_pages;
      });

    builder
      .addCase(deleteProductsThunk.pending, (state, action) => {
        state.loading = { delete: action.meta.arg };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(deleteProductsThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { delete: true };
        state.success = {};
        state.errorMessage = { delete: action.payload?.message };
      })
      .addCase(deleteProductsThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { delete: true };
        state.errorMessage = {};
        productAdapter.removeOne(state, action.meta.arg);
      });
    builder
      .addCase(getProductThunk.pending, (state, action) => {
        state.loading = { get: action.meta.arg };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(getProductThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { get: action.meta.arg };
        state.success = {};
        state.errorMessage = { get: { id: action.payload?.message } };
      })
      .addCase(getProductThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { get: action.meta.arg };
        state.errorMessage = {};
        productAdapter.upsertOne(state, action.payload);
      });

    builder
      .addCase(refreshCurrentProductThunk.pending, (state, action) => {
        state.loading = { refresh: action.meta.arg };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(refreshCurrentProductThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { refresh: action.meta.arg };
        state.success = {};
        state.errorMessage = { refresh: { id: action.payload?.message } };
      })
      .addCase(refreshCurrentProductThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { refresh: action.meta.arg };
        state.errorMessage = {};
        state.currentProduct = action.payload;
        productAdapter.upsertOne(state, action.payload);
      });

    builder
      .addCase(updateProductThunk.pending, (state, action) => {
        state.loading = { update: action.meta.arg.id };
        state.error = {};
        state.success = {};
        state.errorMessage = {};
      })
      .addCase(updateProductThunk.rejected, (state, action) => {
        state.loading = {};
        state.error = { update: action.meta.arg.id };
        state.success = {};
        state.errorMessage = { update: { id: action.payload?.message } };
      })
      .addCase(updateProductThunk.fulfilled, (state, action) => {
        state.loading = {};
        state.error = {};
        state.success = { update: action.meta.arg.id };
        state.errorMessage = {};
        productAdapter.updateOne(state, {
          id: action.meta.arg.id,
          changes: action.payload,
        });
        state.vend_status = "failed";
      });

    builder
      .addCase(deleteProductImageThunk.pending, (state, action) => {
        state.imageStatus.loading = { delete: action.meta.arg.id };
        state.imageStatus.error = {};
        state.imageStatus.success = {};
        state.imageStatus.errorMessage = {};
      })
      .addCase(deleteProductImageThunk.rejected, (state, action) => {
        state.imageStatus.loading = {};
        state.imageStatus.error = { delete: action.meta.arg.id };
        state.imageStatus.success = {};
        state.imageStatus.errorMessage = {
          refresh: { id: action.payload?.message },
        };
      })
      .addCase(deleteProductImageThunk.fulfilled, (state, action) => {
        state.imageStatus.loading = {};
        state.imageStatus.error = {};
        state.imageStatus.success = { delete: action.meta.arg.id };
        state.imageStatus.errorMessage = {};

        productAdapter.updateOne(state, {
          id: action.meta.arg.product.id,
          changes: {
            ...action.meta.arg.product,
            images: action.meta.arg.product.images.filter(
              (item) => item.id !== action.meta.arg.id
            ),
          },
        });
        if (state.currentProduct?.id === action.meta.arg.product.id) {
          state.currentProduct.images = state.currentProduct.images.filter(
            (item) => item.id !== action.meta.arg.id
          );
        }
      });

    builder
      .addCase(deleteProductVideoThunk.pending, (state, action) => {
        state.videoStatus.loading = { delete: action.meta.arg.id };
        state.videoStatus.error = {};
        state.videoStatus.success = {};
        state.videoStatus.errorMessage = {};
      })
      .addCase(deleteProductVideoThunk.rejected, (state, action) => {
        state.videoStatus.loading = {};
        state.videoStatus.error = { delete: action.meta.arg.id };
        state.videoStatus.success = {};
        state.videoStatus.errorMessage = {
          delete: { id: action.payload?.message },
        };
      })
      .addCase(deleteProductVideoThunk.fulfilled, (state, action) => {
        state.videoStatus.loading = {};
        state.videoStatus.error = {};
        state.videoStatus.success = { delete: action.meta.arg.id };
        state.videoStatus.errorMessage = {};

        productAdapter.updateOne(state, {
          id: action.meta.arg.product.id,
          changes: {
            ...action.meta.arg.product,
            videos: action.meta.arg.product.videos.filter(
              (item) => item.id !== action.meta.arg.id
            ),
          },
        });
        if (state.currentProduct?.id === action.meta.arg.product.id) {
          state.currentProduct.videos = state.currentProduct.videos.filter(
            (item) => item.id !== action.meta.arg.id
          );
        }
      });

    builder.addCase(createGstThunk.fulfilled, (state, action) => {
      productAdapter.updateOne(state, {
        id: action.payload.product,
        changes: { gst: action.payload },
      });
    });

    builder.addCase(updateGstThunk.fulfilled, (state, action) => {
      productAdapter.updateOne(state, {
        id: action.payload.product,
        changes: { gst: action.payload },
      });
    });
  },
});
export const productSelector = productAdapter.getSelectors(
  (state) => state.product
);

export const { clearProduct } = productSlice.actions;

export default productSlice.reducer;
